<script setup>
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import { ref, watch } from "vue";
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Head } from '@inertiajs/inertia-vue3';
import { mdiFileTableOutline, mdiPrinter, mdiFilePdfBox } from "@mdi/js";
import CardBox from "@/Components/CardBox.vue";
import CardBoxComponentEmpty from '@/Components/CardBoxComponentEmpty.vue';
import BaseButton from "@/Components/BaseButton.vue";
import VueSelect from "vue3-select-component";

const props = defineProps({
    template: String,
    title: String,
    fileName: String,
    resource: String,
    courses: Array,
    anioLectivo: [String, Number]
});

const loading = ref(false);
const records = ref([]);
const extraData = ref({});
const error = ref('');
const courseStudents = ref([]);

const form = ref({
    template: props.template,
    id_course: 0,
    id_student: 0,
    date_start: '',
    date_end: ''
});

watch(() => form.value.id_course, async (newVal) => {
    form.value.id_student = 0;
    courseStudents.value = [];
    if (newVal > 0) {
        try {
            const res = await axios.post(`/${props.resource}/students`, { id_course: newVal });
            courseStudents.value = res.data;
        } catch (e) {
            console.error('Error fetching students', e);
        }
    }
});

const getRecords = async () => {
    if (!form.value.id_course || form.value.id_course === 0) {
        error.value = 'Debe seleccionar un curso libre.';
        return;
    }
    
    error.value = '';
    loading.value = true;
    records.value = [];
    
    try {
        const response = await axios.post(`/${props.resource}/records`, form.value);
        if (props.template === 'grades' && response.data.records) {
            records.value = response.data.records;
            extraData.value = { tasks: response.data.tasks };
        } else {
            records.value = response.data;
        }
    } catch (e) {
        console.error(e);
        error.value = 'Ocurrió un error al cargar los datos.';
    } finally {
        loading.value = false;
    }
};

const exportFile = async (format) => {
    loading.value = true;
    try {
        const response = await axios({
            url: `/${props.resource}/export`,
            method: 'POST',
            responseType: 'blob',
            data: {
                format: format,
                template: props.template,
                id_course: form.value.id_course,
                records: records.value,
                extraData: extraData.value
            }
        });

        const res = response.data;
        if (res.type === 'application/json') {
            console.error('Error al exportar');
            error.value = 'No se pudo generar la exportación.';
        } else {
            const url = window.URL.createObjectURL(new Blob([res]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', props.fileName + '.' + format);
            document.body.appendChild(link);
            link.click();
        }
    } catch (e) {
        console.error(e);
        error.value = 'Ocurrió un error en la exportación.';
    } finally {
        loading.value = false;
    }
};
</script>

<template>
    <Head :title="title" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader :title="title" :subtitle="'Gestión de Reportes - Cursos Libres'" />
        </template>

        <div>
            <!-- Loader -->
            <div v-if="loading" class="flex flex-col justify-center items-center h-64 absolute inset-0 bg-white/80 z-50">
                <div class="border-slate-300 h-16 w-16 animate-spin rounded-full border-4 border-t-indigo-600 mb-4" />
                <p class="text-indigo-600 font-medium">Procesando datos...</p>
            </div>

            <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 relative">
                
                <!-- Error Alert -->
                <div v-if="error" class="mb-4 p-4 text-red-700 bg-red-100 rounded-lg">
                    {{ error }}
                </div>

                <!-- Filters -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div class="col-span-1 md:col-span-2">
                        <label class="block font-medium text-sm text-slate-700 dark:text-slate-300 mb-1">Curso de Formación</label>
                        <VueSelect 
                            class="select-search-light max-h-[38px] w-full" 
                            v-model="form.id_course" 
                            :options="courses" 
                            :get-option-label="(option) => `${option.label}`" 
                            placeholder="Seleccione un curso..." 
                        />
                    </div>

                    <template v-if="template === 'attendance'">
                        <div>
                            <label class="block font-medium text-sm text-slate-700 dark:text-slate-300 mb-1">Alumno</label>
                            <VueSelect 
                                class="select-search-light max-h-[38px] w-full" 
                                v-model="form.id_student" 
                                :options="courseStudents" 
                                :get-option-label="(option) => `${option.label}`" 
                                placeholder="Todos los alumnos" 
                            />
                        </div>
                        <div>
                            <label class="block font-medium text-sm text-slate-700 dark:text-slate-300 mb-1">Fecha Inicio</label>
                            <input type="date" v-model="form.date_start" class="w-full rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                        </div>
                        <div>
                            <label class="block font-medium text-sm text-slate-700 dark:text-slate-300 mb-1">Fecha Fin</label>
                            <input type="date" v-model="form.date_end" class="w-full rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                        </div>
                    </template>
                </div>

                <!-- Actions -->
                <div class="flex flex-wrap gap-3 mb-6">
                    <BaseButton @click="getRecords" color="info" :icon="mdiFileTableOutline" label="Procesar" />
                    <BaseButton v-if="records.length > 0" @click="exportFile('xlsx')" color="success" :icon="mdiFileTableOutline" label="Exportar a Excel" outline />
                    <BaseButton v-if="records.length > 0" @click="exportFile('pdf')" color="danger" :icon="mdiFilePdfBox" label="Exportar a PDF" outline />
                </div>

                <!-- Results Information -->
                <div v-if="records.length > 0" class="text-sm text-slate-500 mb-4 font-medium">
                    Mostrando {{ records.length }} resultados
                </div>

                <!-- Tables -->
                <CardBox v-if="records.length > 0" class="mb-6 overflow-hidden" has-table>
                    <div class="overflow-x-auto w-full">
                        
                        <!-- Table: Students -->
                        <table v-if="template === 'students'" class="w-full text-left border-collapse">
                            <thead>
                                <tr class="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">#</th>
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Estudiante</th>
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Documento</th>
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Correo</th>
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Celular</th>
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Fecha Inscripción</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
                                <tr v-for="(row, index) in records" :key="row.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                    <td class="p-3 text-sm">{{ index + 1 }}</td>
                                    <td class="p-3 text-sm font-medium">{{ row.fullname }}</td>
                                    <td class="p-3 text-sm">{{ row.num_doc }}</td>
                                    <td class="p-3 text-sm">{{ row.correo || '-' }}</td>
                                    <td class="p-3 text-sm">{{ row.celular || '-' }}</td>
                                    <td class="p-3 text-sm">{{ row.fecha_matricula }}</td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Table: Attendance -->
                        <table v-else-if="template === 'attendance'" class="w-full text-left border-collapse">
                            <thead>
                                <tr class="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">#</th>
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Fecha</th>
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Estudiante</th>
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Estado</th>
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Observación</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
                                <tr v-for="(row, index) in records" :key="row.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                    <td class="p-3 text-sm">{{ index + 1 }}</td>
                                    <td class="p-3 text-sm">{{ row.date }}</td>
                                    <td class="p-3 text-sm font-medium">{{ row.fullname }}</td>
                                    <td class="p-3 text-sm">
                                        <span :class="{
                                            'px-2 py-1 text-xs rounded-full font-medium': true,
                                            'bg-green-100 text-green-700': row.status === 'puntual' || row.status === 'presente',
                                            'bg-yellow-100 text-yellow-700': row.status === 'tarde',
                                            'bg-red-100 text-red-700': row.status === 'falta' || row.status === 'ausente',
                                            'bg-blue-100 text-blue-700': row.status === 'justificado'
                                        }">
                                            {{ row.status.charAt(0).toUpperCase() + row.status.slice(1) }}
                                        </span>
                                    </td>
                                    <td class="p-3 text-sm text-slate-500">{{ row.observation || '-' }}</td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Table: Grades -->
                        <table v-else-if="template === 'grades'" class="w-full text-left border-collapse">
                            <thead>
                                <tr class="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300 sticky left-0 bg-slate-50 dark:bg-slate-800">Estudiante</th>
                                    <th v-for="task in extraData?.tasks" :key="task.id" class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300 text-center">
                                        {{ task.title }}<br>
                                        <span class="text-xs text-slate-400 font-normal">Máx: {{ task.max_points }}</span>
                                    </th>
                                    <th class="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300 text-center bg-indigo-50 dark:bg-indigo-900/30">
                                        Promedio
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
                                <tr v-for="row in records" :key="row.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                    <td class="p-3 text-sm font-medium sticky left-0 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50">
                                        {{ row.fullname }}
                                    </td>
                                    <td v-for="task in extraData?.tasks" :key="task.id" class="p-3 text-sm text-center">
                                        {{ row['task_' + task.id] || 0 }}
                                    </td>
                                    <td class="p-3 text-sm text-center font-bold bg-indigo-50/50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400">
                                        {{ row.promedio }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Certificates / Others -->
                        <div v-else class="p-8 text-center text-slate-500">
                            Plantilla no soportada o en desarrollo.
                        </div>

                    </div>
                </CardBox>
                
                <CardBox v-else-if="!loading && form.id_course > 0 && records.length === 0">
                    <CardBoxComponentEmpty />
                </CardBox>

            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>
